import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Clock, CheckCircle, XCircle, Eye, Building2, Briefcase, Euro } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { companyService } from '@/services/companyService';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type { CompanyInvitation } from '@/types';

export function ReceivedInvitationsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [invitations, setInvitations] = useState<CompanyInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvitation, setSelectedInvitation] = useState<CompanyInvitation | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Assuming 'individual' provider type check is correct from previous code
  const isIndividualProvider = user?.role === 'provider';

  useEffect(() => {
    if (!isIndividualProvider) {
      navigate('/dashboard');
    } else {
      loadInvitations();
    }
  }, [user?.email, isIndividualProvider, navigate]);

  const loadInvitations = async () => {
    if (!user?.email) return;

    setIsLoading(true);
    try {
      const data = await companyService.getReceivedInvitations(user.email);
      setInvitations(data);
    } catch (err) {
      error('Failed to load invitations', 'Please try again.');
      console.error('Failed to load invitations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) return;

      await companyService.acceptInvitation(invitationId);

      // Server-side accept already updates company association in provider profile.

      success('Invitation Accepted', `You have joined ${invitation.companyName}.`);
      await loadInvitations();
      setShowDetailModal(false);

      // Navigate to dashboard after short delay or immediately
      setTimeout(() => navigate('/dashboard'), 1000);

    } catch (err) {
      error('Failed to accept invitation', 'Please try again.');
    }
  };

  const handleRejectInvitation = async (invitationId: string, reason?: string) => {
    try {
      await companyService.rejectInvitation(invitationId, reason);
      success('Invitation Rejected', 'You have declined the invitation.');
      await loadInvitations();
      setShowDetailModal(false);
    } catch (err) {
      error('Failed to reject invitation', 'Please try again.');
    }
  };

  const handleViewDetails = (invitation: CompanyInvitation) => {
    setSelectedInvitation(invitation);
    setShowDetailModal(true);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: CompanyInvitation['status']) => {
    switch (status) {
      case 'pending': return <Badge variant="warning">Pending</Badge>;
      case 'accepted': return <Badge variant="success">Accepted</Badge>;
      case 'rejected': return <Badge variant="danger">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Invitations' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading invitations...</p>
        </div>
      </div>
    );
  }

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const pastInvitations = invitations.filter(inv => inv.status !== 'pending');

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={breadcrumbItems} onBack={() => navigate('/dashboard')} />
        
        <div className="page-header mt-4">
          <h1 className="page-title">Company Invitations</h1>
          <p className="page-description">Manage invitations from companies to join their team.</p>
        </div>

        <div className="space-y-8">
          {/* Active Invitations */}
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary-600" />
              Active Invitations
            </h2>
            {pendingInvitations.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No pending invitations.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {pendingInvitations.map(invitation => (
                  <Card key={invitation.id} className="p-6 hover:shadow-md transition-all border-primary-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{invitation.companyName}</h3>
                          <p className="text-sm text-gray-500">Invited by {invitation.inviterName}</p>
                        </div>
                      </div>
                      <Badge variant="warning">Action Required</Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">Role:</span> {invitation.companyRole}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Euro className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">Rate:</span> €{invitation.proposedHourlyRate}/hr
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 col-span-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">Sent:</span> {formatDate(invitation.createdAt)}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleViewDetails(invitation)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button
                        variant="primary"
                        className="flex-1"
                        onClick={() => handleAcceptInvitation(invitation.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {/* Past Invitations */}
          {pastInvitations.length > 0 && (
            <section>
              <h2 className="text-lg font-bold text-gray-900 mb-4 text-gray-500">History</h2>
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Company</th>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Role</th>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pastInvitations.map(invitation => (
                      <tr key={invitation.id} className="hover:bg-gray-50">
                        <td className="py-4 px-6 text-sm font-medium text-gray-900">{invitation.companyName}</td>
                        <td className="py-4 px-6 text-sm text-gray-600 capitalize">{invitation.companyRole}</td>
                        <td className="py-4 px-6 text-sm text-gray-600">{formatDate(invitation.createdAt)}</td>
                        <td className="py-4 px-6">
                          {getStatusBadge(invitation.status)}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <button
                            onClick={() => handleViewDetails(invitation)}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* Details Modal */}
        {showDetailModal && selectedInvitation && (
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">Invitation Details</h3>
                <button onClick={() => setShowDetailModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary-100">
                    <Building2 className="w-8 h-8 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedInvitation.companyName}</h2>
                  <p className="text-sm text-gray-500">invites you to join as a <span className="font-medium text-gray-900 capitalize">{selectedInvitation.companyRole}</span></p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Employment Type</span>
                    <span className="font-medium text-gray-900 capitalize">{selectedInvitation.employmentType}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Proposed Rate</span>
                    <span className="font-medium text-gray-900">€{selectedInvitation.proposedHourlyRate}/hr</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Commission</span>
                    <span className="font-medium text-gray-900">{selectedInvitation.proposedCommissionRate}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Personal Jobs</span>
                    <span className="font-medium text-gray-900">{selectedInvitation.canTakePersonalJobs ? 'Allowed' : 'Not Allowed'}</span>
                  </div>
                </div>

                {selectedInvitation.message && (
                  <div className="bg-blue-50 text-blue-800 p-4 rounded-lg text-sm italic border border-blue-100">
                    "{selectedInvitation.message}"
                  </div>
                )}

                {selectedInvitation.status === 'pending' && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <Button
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                      onClick={() => handleRejectInvitation(selectedInvitation.id, 'Declined by user')}
                    >
                      Decline
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => handleAcceptInvitation(selectedInvitation.id)}
                    >
                      Accept Job
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}